<template>
  <v-container fluid>
    <base-material-card color="primary" icon="mdi-chart-box" inline class="ml-0 px-5 py-4">
      <template v-slot:after-heading>
        <div class="display-2 font-weight-light">Saneamento do Razão - Painel Gerencial</div>
      </template>

      <div v-if="loading" class="py-12 text-center">
        <v-progress-circular indeterminate color="primary" size="64" width="3" />
        <div class="body-1 grey--text mt-4">Carregando painel gerencial...</div>
      </div>

      <v-alert v-else-if="!execucao" dense outlined type="error" class="my-4">
        Não foi possível carregar os dados do painel gerencial.
      </v-alert>

      <template v-else>
        <v-row align="center" class="my-2">
          <v-col cols="12" md="8">
            <div class="display-1 font-weight-light">
              <span class="grey--text text--darken-1">#{{ execucao.id }}</span>
              <span class="grey--text mx-3 text--lighten-1">|</span>
              {{ execucao.nome }}
              <span class="grey--text mx-3 text--lighten-1">|</span>
              {{ versaoFormatada }}
            </div>
          </v-col>
          <v-col cols="12" md="4" class="text-md-right">
            <v-btn color="primary" depressed @click="$emit('download-arquivo-comparativo')">
              <v-icon left>mdi-download</v-icon>
              Baixar linhas
            </v-btn>

            <v-btn outlined color="primary" class="ml-2" @click="$emit('download-arquivo-ajuste-manual')">
              <v-icon left>mdi-file-edit-outline</v-icon>
              Baixar ajuste manual
            </v-btn>
          </v-col>
        </v-row>

        <v-row class="mb-6">
          <v-col cols="12">
            <div class="align-center body-1 d-flex flex-wrap grey--text text--darken-2">
              <general-status :status="execucao.status" class="mr-4" />
              <span class="d-none d-sm-inline grey--text mx-3 text--lighten-1">|</span>
              <span>
                Executado:
                <strong class="grey--text text--darken-4">{{ execucao.dataExecucao | parseToDateTimeBR }}</strong>
              </span>
              <span class="grey--text mx-3 text--lighten-1">|</span>
              <span>
                Autor:
                <strong class="grey--text text--darken-4">{{ execucao.autor }}</strong>
              </span>
              <span class="grey--text mx-3 text--lighten-1">|</span>
              <span>
                Executado por:
                <strong class="grey--text text--darken-4">{{ execucao.executor }}</strong>
              </span>
            </div>
          </v-col>
        </v-row>

        <v-row class="mb-4">
          <v-col cols="12" class="d-flex justify-end">
            <v-select
              v-model="tipologiaFiltro"
              :items="tipologiasOpcoes"
              item-text="label"
              item-value="value"
              label="Tipologia"
              hide-details
              dense
              outlined
              style="max-width: 280px"
            />
          </v-col>
        </v-row>

        <v-alert v-if="!temDados" dense outlined type="info" color="blue" class="my-4">
          {{ mensagemSemDados }}
        </v-alert>

        <v-row v-if="!temDados">
          <v-col cols="12">
            <v-card outlined class="ma-0 pa-8 text-center" color="#F7FAFA">
              <v-icon size="72" color="grey lighten-1">mdi-chart-box-outline</v-icon>
              <div class="font-weight-medium grey--text headline mt-4 text--darken-2">{{ mensagemSemDados }}</div>
              <div v-if="tipologiaFiltro" class="body-2 grey--text mt-2">
                Selecione outra tipologia ou escolha "Todas" para visualizar o painel completo.
              </div>
            </v-card>
          </v-col>
        </v-row>

        <template v-if="temDados">
          <v-row align="stretch">
            <v-col cols="12" md="4" class="d-flex">
              <v-card outlined class="flex-grow-1 ma-0 pa-5">
                <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">Total de linhas</div>
                <div class="font-weight-medium headline mt-2 primary--text">
                  {{ dadosPainel.totalLinhas | parseNumberToIntegerBR }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="d-flex">
              <v-card outlined class="flex-grow-1 ma-0 pa-5">
                <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">Linhas com diferenças</div>
                <div class="font-weight-medium headline mt-2 primary--text">
                  {{ dadosPainel.linhasComDiferencas | parseNumberToIntegerBR }}
                </div>
                <div class="body-2 grey--text mt-2">
                  {{ percentualLinhasComDiferencas | parseNumberToDecimalBR }}% do total de linhas
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="4" class="d-flex">
              <v-card outlined class="flex-grow-1 ma-0 pa-5">
                <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">Campo com mais diferenças</div>
                <div class="align-center body-1 d-flex flex-wrap font-weight-medium grey--text mb-1 mt-2 text--darken-3">
                  <span>{{ dadosPainel.campoMaisDiferencas.original }}</span>
                  <v-icon class="mx-1" small>mdi-arrow-right</v-icon>
                  <span>{{ dadosPainel.campoMaisDiferencas.revisado }}</span>
                </div>
                <div class="font-weight-bold headline mt-2 primary--text">
                  {{ dadosPainel.campoMaisDiferencas.qtdDiferencas | parseNumberToIntegerBR }}
                </div>
                <div class="body-2 grey--text mt-2">
                  {{ dadosPainel.campoMaisDiferencas.percentualTotal | parseNumberToDecimalBR }}% do total de linhas
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row align="stretch" class="mt-4">
            <v-col cols="12" md="6" class="d-flex">
              <v-card outlined class="flex-grow-1 ma-0 pa-5">
                <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">Total de erros</div>
                <div class="error--text font-weight-medium headline mt-2">
                  {{ dadosPainel.totalErros | parseNumberToIntegerBR }}
                </div>
              </v-card>
            </v-col>
            <v-col cols="12" md="6" class="d-flex">
              <v-card outlined class="flex-grow-1 ma-0 pa-5">
                <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">Total de alertas</div>
                <div class="font-weight-medium headline mt-2 orange--text text--darken-2">
                  {{ dadosPainel.totalAlertas | parseNumberToIntegerBR }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-4">
            <v-col cols="12">
              <v-card outlined class="ma-0 pa-5">
                <div class="font-weight-medium mb-4 subtitle-1">Valor total do mês — ROA x Adições</div>
                <v-row align="center">
                  <v-col cols="12" md="5">
                    <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">ROA</div>
                    <div class="font-weight-medium headline mt-2 primary--text">
                      {{ dadosPainel.valorRoaMes | parseNumberToDecimalBR }}
                    </div>
                  </v-col>
                  <v-col cols="12" md="2" class="d-md-flex d-none justify-center text-center">
                    <v-icon large color="grey">mdi-swap-horizontal</v-icon>
                  </v-col>
                  <v-col cols="12" md="5">
                    <div class="caption font-weight-medium grey--text text--darken-1 text-uppercase">Adições</div>
                    <div class="font-weight-medium headline mt-2 primary--text">
                      {{ dadosPainel.valorAdicoesMes | parseNumberToDecimalBR }}
                    </div>
                  </v-col>
                </v-row>
                <v-divider class="my-4" />
                <div class="body-1 grey--text text--darken-2">
                  Diferença:
                  <strong class="primary--text">
                    {{ diferencaRoaAdicoes | parseNumberToDecimalBR }}
                  </strong>
                  <span class="ml-2">({{ percentualDiferencaRoaAdicoes | parseNumberToDecimalBR }}% sobre Adições)</span>
                </div>
              </v-card>
            </v-col>
          </v-row>

          <v-row class="mt-6">
            <v-col cols="12">
              <v-card outlined class="ma-0 pa-4">
                <div class="font-weight-medium mb-4 subtitle-1">Linhas com diferença por campo revisado</div>
                <highcharts v-if="temDadosGrafico" :options="graficoBarrasOptions" />
                <div v-else class="grey--text py-8 text-center">
                  <v-icon size="48" color="grey lighten-1">mdi-chart-bar</v-icon>
                  <div class="body-1 mt-3">Não há diferenças por campo para exibir no gráfico.</div>
                </div>
              </v-card>
            </v-col>
          </v-row>
        </template>

        <v-row v-if="temDados" class="mt-6">
          <v-col cols="12">
            <v-card outlined class="ma-0 pa-4">
              <div class="align-center d-flex flex-wrap mb-4">
                <div class="font-weight-medium subtitle-1">Detalhamento por campo</div>
                <v-text-field
                  v-model="search"
                  append-icon="mdi-magnify"
                  label="Procurar campo"
                  hide-details
                  dense
                  class="ml-auto"
                  style="max-width: 280px"
                />
              </div>
              <v-data-table
                :headers="headersDetalhamento"
                :items="dadosPainel.detalhamento"
                :search="search"
                :items-per-page="10"
                :footer-props="{ itemsPerPageText: 'Itens por Página' }"
                :header-props="{ sortByText: 'Ordenar Por' }"
                no-data-text="Nenhum campo com diferença para exibir"
                no-results-text="Nenhum campo encontrado na busca"
                dense
                mobile-breakpoint="0"
              >
                <template v-slot:[`item.original`]="{ item }">
                  <span class="font-weight-medium">{{ item.original }}</span>
                </template>
                <template v-slot:[`item.revisado`]="{ item }">
                  <span class="font-weight-medium">{{ item.revisado }}</span>
                </template>
                <template v-slot:[`item.qtdDiferencas`]="{ item }">
                  {{ item.qtdDiferencas | parseNumberToIntegerBR }}
                </template>
                <template v-slot:[`item.percentualTotal`]="{ item }">
                  {{ item.percentualTotal | parseNumberToDecimalBR }}%
                </template>
                <template v-slot:[`item.barra`]="{ item }">
                  <v-progress-linear
                    :value="item.percentualTotal"
                    height="10"
                    rounded
                    color="primary"
                    background-color="grey lighten-3"
                  />
                </template>
              </v-data-table>
            </v-card>
          </v-col>
        </v-row>

        <v-row class="mt-2">
          <v-col cols="12">
            <v-card outlined class="ma-0 pa-4">
              <div class="align-center d-flex flex-wrap mb-3">
                <div class="font-weight-medium subtitle-1">Ajustes manuais</div>
                <v-spacer />
                <v-text-field
                  v-if="ajustesManuais.length"
                  v-model="searchAjustes"
                  append-icon="mdi-magnify"
                  label="Buscar"
                  hide-details
                  dense
                  outlined
                  class="ml-auto"
                  style="max-width: 280px"
                />
              </div>
              <v-data-table
                v-if="loadingAjustes || ajustesManuais.length"
                :headers="headersAjustesManuais"
                :items="ajustesManuais"
                :search="searchAjustes"
                :loading="loadingAjustes"
                :items-per-page="10"
                :footer-props="{ itemsPerPageText: 'Itens por Página' }"
                :header-props="{ sortByText: 'Ordenar Por' }"
                no-results-text="Nenhum ajuste encontrado na busca"
                dense
                mobile-breakpoint="0"
              />
              <div v-else class="py-4 text--secondary text-center">Nenhum ajuste manual encontrado para esta execução</div>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </base-material-card>
  </v-container>
</template>

<script>
  import Highcharts from 'highcharts';
  import HighchartsNoData from 'highcharts/modules/no-data-to-display';
  import SaneamentosRazaoService from '@/services/SaneamentosRazaoService';

  HighchartsNoData(Highcharts);

  export default {
    name: 'DashboardGerencialSaneamentoRazao',

    components: {
      GeneralStatus: () => import('@/components/general/GeneralStatus.vue')
    },

    props: {
      execucao: {
        type: Object,
        default: null
      },
      loading: {
        type: Boolean,
        default: false
      }
    },

    emits: ['download-arquivo-comparativo', 'download-arquivo-ajuste-manual'],

    data() {
      return {
        search: '',
        searchAjustes: '',
        loadingAjustes: false,
        ajustesManuais: [],
        tipologiaFiltro: null,
        tipologiasOpcoes: [
          { value: null, label: 'Todas' },
          { value: 'RD', label: 'RD' },
          { value: 'ME', label: 'ME' },
          { value: 'SE/LT', label: 'SE/LT' },
          { value: 'ANE', label: 'ANE' }
        ],
        headersDetalhamento: [
          { text: 'Original', value: 'original', width: '140px' },
          { text: 'Revisado', value: 'revisado', width: '180px' },
          { text: 'Qtd Diferenças', value: 'qtdDiferencas', align: 'end', width: '130px' },
          { text: '% do Total', value: 'percentualTotal', align: 'end', width: '100px' },
          { text: '', value: 'barra', sortable: false, width: '160px' }
        ]
      };
    },

    computed: {
      headersAjustesManuais() {
        if (!this.ajustesManuais.length) return [];
        return Object.keys(this.ajustesManuais[0]).map((key) => ({
          text: key.toUpperCase(),
          value: key
        }));
      },

      versaoFormatada() {
        const mes = String(this.execucao.mes).padStart(2, '0');
        return `${mes}/${this.execucao.ano} (${this.execucao.versao})`;
      },

      percentualLinhasComDiferencas() {
        if (!this.dadosPainel.totalLinhas) return 0;
        return (this.dadosPainel.linhasComDiferencas / this.dadosPainel.totalLinhas) * 100;
      },

      temDados() {
        return this.dadosPainel.totalLinhas > 0;
      },

      temDadosGrafico() {
        return this.dadosPainel.detalhamento.length > 0;
      },

      tipologiaSelecionadaLabel() {
        const opcao = this.tipologiasOpcoes.find((item) => item.value === this.tipologiaFiltro);
        return opcao ? opcao.label : this.tipologiaFiltro;
      },

      mensagemSemDados() {
        if (this.tipologiaFiltro) {
          return `Nenhum dado encontrado para a tipologia ${this.tipologiaSelecionadaLabel}.`;
        }
        return 'Esta execução não possui linhas para análise.';
      },

      dadosPainel() {
        if (!this.execucao) {
          return this.dadosPainelVazio();
        }

        if (!this.tipologiaFiltro) {
          return {
            totalLinhas: this.execucao.totalLinhas,
            linhasComDiferencas: this.execucao.linhasComDiferencas,
            totalErros: this.execucao.totalErros,
            totalAlertas: this.execucao.totalAlertas,
            valorRoaMes: this.execucao.valorRoaMes,
            valorAdicoesMes: this.execucao.valorAdicoesMes,
            campoMaisDiferencas: this.execucao.campoMaisDiferencas,
            detalhamento: this.execucao.detalhamento || []
          };
        }

        const tipologias = this.execucao.tipologias || [];
        const tipologia = tipologias.find((item) => item.codigo === this.tipologiaFiltro);
        if (!tipologia) {
          return this.dadosPainelVazio();
        }

        return {
          ...tipologia,
          detalhamento: tipologia.detalhamento || []
        };
      },

      diferencaRoaAdicoes() {
        return this.dadosPainel.valorRoaMes - this.dadosPainel.valorAdicoesMes;
      },

      percentualDiferencaRoaAdicoes() {
        if (!this.dadosPainel.valorAdicoesMes) return 0;
        return (this.diferencaRoaAdicoes / this.dadosPainel.valorAdicoesMes) * 100;
      },

      graficoBarrasOptions() {
        const totalLinhas = this.dadosPainel.totalLinhas;
        const topCampos = [...this.dadosPainel.detalhamento].sort((a, b) => a.qtdDiferencas - b.qtdDiferencas).slice(-12);

        return {
          chart: {
            type: 'bar',
            height: Math.max(360, topCampos.length * 32),
            marginLeft: 200,
            spacingLeft: 10
          },
          title: { text: null },
          credits: { enabled: false },
          legend: { enabled: false },
          xAxis: {
            categories: topCampos.map((item) => item.revisado),
            title: { text: null },
            labels: {
              style: { fontSize: '13px' },
              align: 'right',
              reserveSpace: true
            }
          },
          yAxis: {
            min: 0,
            title: { text: 'Linhas com diferença' },
            labels: {
              formatter() {
                return Number(this.value).toLocaleString('pt-BR');
              }
            }
          },
          tooltip: {
            headerFormat: '<span style="font-size:11px"><b>{point.key}</b></span><br/>',
            pointFormatter() {
              const pct = totalLinhas > 0 ? ((this.y / totalLinhas) * 100).toFixed(2) : '0.00';
              return `<b>${Number(this.y).toLocaleString('pt-BR')}</b> linhas (${pct}% do total)`;
            }
          },
          plotOptions: {
            bar: {
              dataLabels: {
                enabled: true,
                formatter() {
                  return Number(this.y).toLocaleString('pt-BR');
                },
                style: { fontSize: '12px', fontWeight: 'normal' }
              }
            }
          },
          series: [
            {
              name: 'Linhas com diferença',
              data: topCampos.map((item) => item.qtdDiferencas),
              color: '#9c27b0'
            }
          ],
          lang: {
            noData: 'Não há diferenças por campo para exibir no gráfico.'
          },
          noData: {
            style: {
              fontWeight: 'bold',
              fontSize: '15px',
              color: '#303030'
            }
          }
        };
      }
    },

    methods: {
      dadosPainelVazio() {
        return {
          totalLinhas: 0,
          linhasComDiferencas: 0,
          totalErros: 0,
          totalAlertas: 0,
          valorRoaMes: 0,
          valorAdicoesMes: 0,
          campoMaisDiferencas: { original: '-', revisado: '-', qtdDiferencas: 0, percentualTotal: 0 },
          detalhamento: []
        };
      },

      carregarAjustesManuais() {
        if (!this.execucao || !this.execucao.id) {
          this.ajustesManuais = [];
          return;
        }

        this.loadingAjustes = true;
        SaneamentosRazaoService.listarAjustesManuais(this.execucao.id)
          .then((response) => {
            this.ajustesManuais = response.data.data || [];
          })
          .catch((error) => {
            console.error(error);
            this.ajustesManuais = [];
          })
          .finally(() => {
            this.loadingAjustes = false;
          });
      }
    },

    watch: {
      execucao: {
        handler() {
          this.carregarAjustesManuais();
        },
        immediate: true
      }
    }
  };
</script>
